import { Component, ElementRef, Inject, Input, OnInit } from '@angular/core';
import { featureGroup, Layer, Map, Polygon, tileLayer } from 'leaflet';
import { ReplaySubject, Subject } from 'rxjs';
import { FieldService } from '../../stores/field.service';
import { ConfigService, Crop, Field, FieldCrop } from 'shared';
import * as L from 'leaflet';
import '@geoman-io/leaflet-geoman-free';
import 'leaflet.pattern';
import 'leaflet-easybutton';
import DataSource from 'devextreme/data/data_source';
import { GoldenLayoutContainer } from 'ngx-golden-layout';
import GoldenLayout from 'golden-layout';
import { FieldCropService } from '../../stores/field-crop.service';
import { forEach } from 'lodash';
import cropIcon from '@iconify/icons-mdi/sprout-outline';
import fieldIcon from '@iconify/icons-mdi/focus-field';
import { MapLayersControlToolbar } from '../../map/map-layers-control/map-layers-control.component';
import { FieldIndexService } from '../../services/field-index.service';
import { lineIntersect } from '@turf/turf';
import dxSelectBox from 'devextreme/ui/select_box';
import { CampaignService } from '../../stores/campaign.service';

export class FieldMapFilter {
  search: string;
  fieldTooltip: string;
  domain: number;
  campaign: number;
}

@Component({
  selector: 'app-field-editor-map',
  templateUrl: './field-editor-map.component.html',
  styleUrls: ['./field-editor-map.component.scss']
})

export class FieldEditorMapComponent {
  // Fields
  fieldsDs: DataSource;
  fieldsLayer = featureGroup();
  fieldsLayersIndex: { [fieldId: number]: { field: Field, layer: Layer } } = {};

  // Fields crops
  fieldCropsDs: DataSource;
  fieldCropsByField: { [fieldId: number]: Layer[] };
  fieldCropsLayer = featureGroup();
  fieldCropsStripes: L.StripePattern[] = [];

  // Filter
  filter = new FieldMapFilter();

  mapLayersToolbar: MapLayersControlToolbar;

  @Input()
  field: Field;
  @Input()
  crop: FieldCrop;
  @Input()
  fieldCrops: FieldCrop[] = [];


  mapOptions;
  map: Map;
  mapReady = new ReplaySubject<Map>(1);
  mapReady$ = this.mapReady.asObservable();

  isLoading = true;

  // View
  viewMode: 'field' | 'crop' = 'field';
  private viewModeSubject = new Subject<string>();
  viewModeSubject$ = this.viewModeSubject.asObservable();

  // Edit
  editFieldSubject = new Subject<{ layer: Polygon, field: Field }>();
  editFieldCropSubject = new Subject<{ layer: Polygon, field: Field, fieldCrop: FieldCrop }>();

  layers = [
    {
      name: 'Hybride',
      layer: tileLayer('https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}&hl=ar-MA&gl=MA', {
        maxZoom: 21,
        subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
      }),
    },
    {
      name: 'Satellite',
      layer: tileLayer('https://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}&hl=ar-MA&gl=MA', {
        maxZoom: 21,
        subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
      }),
    },
  ];

  constructor(
    public host: ElementRef,
    private config: ConfigService,
    private fieldService: FieldService,
    private fieldIndexService: FieldIndexService,
    private fieldCropService: FieldCropService,
    @Inject(GoldenLayoutContainer) public container: GoldenLayout.Container,
  ) {
    this.mapOptions = {
      layers: this.layers.map(l => l.layer),
      zoom: 7,
      minZoom: 0,
      maxZoom: 21,
      maxNativeZoom: 21,
      preferCanvas: false,
      center: [33.626756199999996, -7.4964748]
    };

    // Create fields datasource
    this.fieldsDs = fieldService.newDataSource({
      paginate: false,
      onLoadingChanged: (isLoading) => {
        if (!isLoading) {
          this.addFieldsToLayer();
        }
      }
    });

    // Create fields crops datasource
    this.fieldCropsDs = this.fieldCropService.newDataSource({
      paginate: false,
      onLoadingChanged: (isLoading) => {
        if (!isLoading) {
          this.addFieldsCropsToLayer();
        }
      }
    });

    // Bindings
    this.resetFilter = this.resetFilter.bind(this);
  }

  onMapReady(map: Map) {
    this.map = map;
    this.mapReady.next(map);
    L.control.scale({ imperial: false }).addTo(map);

    setTimeout(() => {
      Promise.all([
        this.fieldIndexService.load(),
        this.fieldsDs.load(),
        this.fieldCropsDs.load()]
      ).then(() => {
        const fields = this.fieldsDs.items();
        const fieldCrops = this.fieldCropsDs.items();
        this.isLoading = false;
        if (!this.field && !this.crop && fields.length && fieldCrops.length) {
          this.map.fitBounds(this.fieldsLayer.getBounds());
        }
      });
    }, 500);

    this.map.pm.setLang('fr');

    // @ts-ignore
    this.map.pm.setGlobalOptions({
      templineStyle: {
        weight: 2,
        color: '#1a8b00'
      },
      hintlineStyle: {
        weight: 2,
        color: '#1a8b00',
        dashArray: [5, 5]
      },
      snapMiddle: true,
    } as L.PM.GlobalOptions);
    if (this.crop) {
      map.addLayer(this.fieldCropsLayer);
    } else {
      map.addLayer(this.fieldsLayer);
    }

  }

  private addFieldsToLayer() {
    const fields = this.fieldsDs.items();

    // Clear all fields
    this.fieldsLayer.clearLayers();
    this.fieldsLayersIndex = {};

    fields.forEach(f => {
      this.addFieldToLayer(f);
    });
  }

  private addFieldToLayer(field: Field) {
    const polygon = field.createLayer({
      // @ts-ignore
      pmIgnore: true,
      weight: 2,
      opacity: 0.4,
      fillOpacity: 0.3,
      color: field.color,
    });

    if (!this.crop) {
      polygon.on('click', () => {
        this.fieldEditStart(polygon, field);
      });
    }

    // Main field
    if (this.field && field.id === this.field.id) {
      this.map.fitBounds(polygon.getBounds(), {
        padding: [50, 50],
        maxZoom: 20,
      });
    }

    polygon.bindTooltip(field.name, {
      direction: 'center',
      opacity: 0.6,
    });

    this.fieldsLayersIndex[field.id] = { field, layer: polygon };
    this.fieldsLayer.addLayer(polygon);
  }

  private addFieldsCropsToLayer() {
    const crops = this.fieldCropsDs.items();
    // Clear all fields
    this.fieldCropsLayer.clearLayers();
    this.fieldCropsByField = {};
    this.fieldCropsStripes.forEach(s => s.remove());

    crops.forEach(c => {
      this.addFieldCropToLayer(c);
    });
  }

  private addFieldCropToLayer(fieldCrop: FieldCrop) {
    // Create stripe
    const stripe = new L.StripePattern({ color: fieldCrop.color, angle: -45 });
    stripe.addTo(this.map);

    // Create polygon
    const polygon = fieldCrop.createLayer({
      // @ts-ignore
      pmIgnore: true,
      weight: 2,
      opacity: 0.4,
      fillOpacity: 0.3,
      color: fieldCrop.color,
      // @ts-ignore
      fillPattern: stripe,
    });

    if (this.crop) {
      polygon.on('click', () => {
        this.fieldCropEditStart(polygon, fieldCrop);
      });
    }

    // Main corps
    if (this.crop && fieldCrop.id === this.crop.id) {
      this.map.fitBounds(polygon.getBounds(), {
        padding: [50, 50],
        maxZoom: 20,
      });
    }

    polygon.bindTooltip(fieldCrop.name, {
      direction: 'center',
      opacity: 0.6,
    });

    // Add to group
    const fieldId = fieldCrop.field_id;
    this.fieldCropsByField[fieldId] = this.fieldCropsByField[fieldId] || [];
    this.fieldCropsByField[fieldId].push(polygon);

    // Add to layer
    this.fieldCropsLayer.addLayer(polygon);
  }

  fieldEditStart(layer: Polygon, field: Field) {
    this.editFieldSubject.next({ layer, field });
  }

  fieldCropEditStart(layer: Polygon, fieldCrop: FieldCrop) {
    const field = this.fieldsDs.items().find(item => {
      return item.id === fieldCrop.field_id
    });

    this.editFieldCropSubject.next({ layer, field, fieldCrop });
  }

  fieldEditDone({ field, isDeleted }) {
    if (!isDeleted) {
      this.fieldIndexService.update(field);
      this.addFieldToLayer(field);
    } else {
      this.fieldIndexService.remove(field);
    }
  }

  fieldCropEditDone({ field, isDeleted }) {
    if (!isDeleted) {
      this.fieldIndexService.update(field);
      this.addFieldCropToLayer(field.field_crop);
    } else {
      this.fieldIndexService.remove(field);
    }
  }

  mapLayersControlLoaded(items: MapLayersControlToolbar) {
    this.mapLayersToolbar = items;
    items.add('selector', {
      location: 'before',
      locateInMenu: 'never',
      widget: 'dxButtonGroup',
      options: {
        items: [
          {
            action: 'fields',
            hint: 'Afficher les parcelles',
            icon: `<svg  width="18px" height="18px" viewBox="0 0 24 24">${fieldIcon.body}</svg>`
          },
          {
            action: 'crops',
            hint: 'Afficher les cultures',
            icon: `<svg  width="18px" height="18px" viewBox="0 0 24 24">${cropIcon.body}</svg>`
          }
        ],
        keyExpr: 'action',
        stylingMode: 'text',
        selectedItemKeys: this.crop ? ['crops'] : ['fields'],
        selectionMode: 'multiple',
        onSelectionChanged: (e) => {
          this.filterLayerSelectorChanged(e);
        }
      }
    });

    items.add('fieldFilter', {
      location: 'before',
      locateInMenu: 'never',
      widget: 'dxButton',
      options: {
        elementAttr: { id: 'fieldMapFilter' },
        icon: 'filter',
        text: 'Filter',
      }
    });
  }

  filterFieldChange(e: any) {
    switch (e.dataField) {
      case 'fieldTooltip':
        this.changeFieldsTooltip(e.value);
        break;
      case 'search':
        this.search(e.value);
        break;
    }
  }

  resetFilter() {
    this.isLoading = true;
    this.filter.domain = 0;
    this.filter.search = '';
    this.fieldsDs.filter(null);
    this.fieldCropsDs.filter(null);
    Promise.all([
      this.fieldsDs.reload(),
      this.fieldCropsDs.reload()]
    ).then(() => {
      this.isLoading = false;
    });
  }

  private changeFieldsTooltip(value: any) {
    let labelValue: (field: Field) => string;
    switch (value) {
      case 'Nom':
        labelValue = f => f.name;
        break;
      case 'Code':
        labelValue = f => f.code;
        break;
      case 'Nom (ID)':
        labelValue = f => `${f.name} (${f.id})`;
        break;
      case 'Nom (Code)':
        labelValue = f => `${f.name} (${f.code})`;
        break;
    }
    forEach(this.fieldsLayersIndex, ({ field, layer }, fieldId) => {
      layer.bindTooltip(labelValue(field), {
        direction: 'center',
        opacity: 0.6,
      });
    });
  }

  domainChange(domain: number) {
    if (domain === 0) {
      return;
    }

    this.isLoading = true;
    this.fieldsDs.filter([['domain_id', '=', domain]]);
    this.fieldsDs.reload().then((result) => {
      if (result.length) {
        this.map.fitBounds(this.fieldsLayer.getBounds());
      }
      this.isLoading = false;
    });
  }

  campaignChange(campaignId: number) {
    if (campaignId == 0) {
      return;
    }

    this.isLoading = true;
    this.fieldCropsDs.filter(['campaign_id', '=', campaignId]);

    this.fieldCropsDs.reload().then((result) => {
      if (result.length && this.fieldCropsLayer.getBounds().isValid()) {
        this.map?.fitBounds(this.fieldCropsLayer?.getBounds());
      }
      this.isLoading = false;
    });
  }

  private search(value: any) {
    this.isLoading = true;
    this.fieldsDs.filter([
      ['name', 'contains', value], 'or',
      ['code', 'contains', value], 'or',
      ['id', '=', value],
    ]);
    this.fieldsDs.reload().then((result) => {
      this.isLoading = false;
    });
  }

  filterLayerSelectorChanged(e: any) {
    e.removedItems.forEach(d => {
      switch (d.action) {
        case 'fields':
          this.fieldsLayer.remove();
          break;
        case 'crops':
          this.fieldCropsLayer.remove();
          break;
      }
    });
    e.addedItems.forEach(d => {
      switch (d.action) {
        case 'fields':
          this.fieldsLayer.addTo(this.map);
          break;
        case 'crops':
          this.fieldCropsLayer.addTo(this.map);
          break;
      }
    });
  }
}
